import './App.scss'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import LoginView from './views/Login/LoginView'
import { useSessionQuery } from './api/api'
import SignupView from 'views/Signup/SignupView'
import PrivateRoute from 'components/PrivateRoute/PrivateRoute'
import DashboardView from 'views/Dashboard/DashboardView'
import LibraryView from 'views/Library/LibraryView'
import TestView from './views/TestView/TestView'
import PortfoliosView from './views/PortfoliosView/PortfoliosView'
import PortfolioEditorView from './views/PortfolioEditorView/PortfolioEditorView'
import { useEffect, useRef, useState } from 'react'
import ChallengeView from './views/Challenge/ChallengeView'
import LogoutView from './views/Logout/LogoutView'
import LoadingBlocker from './components/LoadingSpinner/LoadingBlocker'
import AccountSettingsView from './views/AccountSettings/AccountSettingsView'
import ForgotPasswordView from './views/ForgotPassword/ForgotPasswordView'
import ResetPasswordView from './views/ResetPassword/ResetPasswordView'
import 'fonts/School_Days.woff2'
import 'fonts/School_Days_Thin.woff2'
import 'fonts/School_Days_Bold.woff2'
import useQuery from './utils/hooks/useQuery'
import PaymentConfirmModal from './components/GeneralModal/PaymentConfirmModal'
import SubscriptionRequiredModal from './components/Subscription/SubscriptionRequiredModal'

const SESSION_CHECK_INTERVAL = 1000 * 60 * 60
function App() {
  const { data: user, isLoading, refetch } = useSessionQuery()
  const isAuthenticated = user?.id
  const queryParams = useQuery()
  const navigate = useNavigate()
  const [showPaymentConfirmedModal, setShowPaymentConfirmedModal] = useState(
    queryParams?.success === 'true',
  )

  useEffect(() => {
    const sessionInterval = setInterval(async () => {
      if (isAuthenticated) {
        const results = await refetch()
        if (results.status === 'rejected') {
          navigate('/logout')
        }
      }
    }, SESSION_CHECK_INTERVAL)
    return () => clearInterval(sessionInterval)
  }, [isAuthenticated])

  return (
    <div className="app">
      <div className="body dark:bg-gray">
        {isLoading ? (
          <LoadingBlocker />
        ) : (
          <>
            <Routes>
              <Route path="/login" element={<LoginView user={user} />} />
              <Route path="/signup" element={<SignupView user={user} />} />
              <Route
                path="/challenge"
                element={<ChallengeView user={user} />}
              />
              <Route path="/logout" element={<LogoutView user={user} />} />
              <Route path="/forgotPassword" element={<ForgotPasswordView />} />
              <Route
                path="/resetPassword"
                element={<ResetPasswordView user={user} />}
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <DashboardView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/library"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <LibraryView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/portfolios"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <PortfoliosView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/portfolios/:id"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <PortfolioEditorView user={user} />
                  </PrivateRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <PrivateRoute isAuthenticated={isAuthenticated}>
                    <AccountSettingsView user={user} />
                  </PrivateRoute>
                }
              />

              <Route path="/test" element={<TestView />} />
              <Route path="*" element={<Navigate to="/library" />} />
            </Routes>
            {showPaymentConfirmedModal ?
              <PaymentConfirmModal
                isOpen={showPaymentConfirmedModal}
                onRequestClose={() => setShowPaymentConfirmedModal(false)}
              /> : null
            }

            <SubscriptionRequiredModal
              isOpen={user?.hasSubscribed && !user?.isSubscribed}
            />
          </>
        )}
      </div>
      <Tooltip />
    </div>
  )
}

const Tooltip = () => {
  const ref = useRef(null)
  const contentRef = useRef(null)
  useEffect(() => {
    const onHover = (evt) => {
      const target = evt.target.closest('[data-tooltip]')
      const element = ref.current
      if (ref.current && contentRef.current && target) {
        const bounds = target.getBoundingClientRect()
        element.style.top = `${bounds.top}px`
        element.style.left = `${bounds.left}px`
        element.style.width = `${bounds.width}px`
        element.style.height = `${bounds.height}px`
        element.dataset.offset = target.dataset.tooltipOffset
        contentRef.current.innerText = target.dataset.tooltip

        const refBounds = contentRef.current.getBoundingClientRect()
        const overRightEdge = refBounds.right - window.innerWidth
        if (overRightEdge > 0) {
          element.style.left = `${parseInt(element.style.left, 10) - overRightEdge
            }px`
        }
      } else {
        ref.current.style.top = '-1000px'
      }
    }
    if (ref.current && contentRef.current) {
      document.addEventListener('mousemove', onHover)
    }

    return () => document.removeEventListener('mousemove', onHover)
  }, [ref.current])
  return (
    <div ref={ref} className="global-tooltip-container">
      <div ref={contentRef} className="global-tooltip"></div>{' '}
    </div>
  )
}

export default App
